import LocalStorageService from 'services/LocalStorageService';
import TippingSoundService from 'services/TippingSoundService';
import ChatStore from 'stores/ChatStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import {
  SOUND_NOTIFICATION_TYPES,
  TOKEN_RANGE,
} from 'enum/helpers';
import { STREAM_VOLUME_KEY } from 'enum/videoPlayer';
import { GAMES } from 'utils/consts';
import ThemeService from 'themes/ThemeService';


const PRIVATE_MESSAGE_USER_COOLDOWN = 5000;
const PRIVATE_MESSAGE_OVERALL_COOLDOWN = 500;
const DEFAULT_TIPPING_SOUND_VOLUME = 50;

/**
 * This class handles the logic of the broadcast notifications
 * (tipping, games, private messages, private show requests, etc.)
 */
class BroadcastNotificationsService {
  /** Map of latest notification timestamps for each user */
  lastPrivateChatNotificationTimestamps = new Map();
  lastTippingMessageTimestamp = 0;
  lastBoostMessageTimestamp = 0;
  lastGameMessageTimestamp = 0;
  isTippingSoundMuted = false;
  tippingSoundVolume = parseInt(LocalStorageService.get(STREAM_VOLUME_KEY), 10) || DEFAULT_TIPPING_SOUND_VOLUME;

  get lastPrivateMessageNotificationTimestamp() {
    return Math.max(...this.lastPrivateChatNotificationTimestamps.values()) || 0;
  }

  /**
   * Play a sound for notice of new private message if it's needed
   * and remember the timestamp of the last notification for the sender
   * @param {Object} message Private message
   */
  handlePrivateMessageNotificationSound(message) {
    const { senderOriginalUsername: userName, timestamp } = message;

    const isOutgoingMessage = UserStore.user.username === userName;
    const isFromSilencedUser =
      ProfileStore.silenceList.totalUsersCount > 0 &&
      ProfileStore.silencedListUsernames?.includes(userName);
    const isChatOpened = ChatStore.activeConversation === userName;
    const isItFitsUserCooldown =
      timestamp - (this.lastPrivateChatNotificationTimestamps.get(userName) || 0) > PRIVATE_MESSAGE_USER_COOLDOWN;
    const isItFitsOverallCooldown =
      timestamp - this.lastPrivateMessageNotificationTimestamp > PRIVATE_MESSAGE_OVERALL_COOLDOWN;

    if (
      !isOutgoingMessage &&
      !isFromSilencedUser &&
      isChatOpened &&
      isItFitsUserCooldown &&
      isItFitsOverallCooldown
    ) {
      this.lastPrivateChatNotificationTimestamps.set(userName, Date.now());
      this.playNotificationSound();
    }
  }

  /**
   * Play tipping sound if needed
   * @param {*} message Message object from Firebase
   */
  handleTippingSound = (message) => {
    const isBroadcastWindow = location.pathname === '/broadcast';
    if (message.timestamp <= this.lastTippingMessageTimestamp) {
      return;
    }
    if (this.isTippingSoundMuted && !isBroadcastWindow) {
      return;
    }
    const parsedNotificationExtra = JSON.parse(message.notificationExtra);
    this.lastTippingMessageTimestamp = message.timestamp;
    const parsedTokenValue = parseInt(message.gameName ? parsedNotificationExtra?.p : message.notificationExtra, 10);
    const tierOneTokens = parsedTokenValue < TOKEN_RANGE.LOWEST_SOUND_TOKEN_AMOUNT;
    const tierTwoTokens =
      parsedTokenValue >= TOKEN_RANGE.LOWEST_SOUND_TOKEN_AMOUNT &&
      parsedTokenValue < TOKEN_RANGE.LOW_SOUND_TOKEN_AMOUNT;
    const tierThreeTokens =
      parsedTokenValue >= TOKEN_RANGE.LOW_SOUND_TOKEN_AMOUNT &&
      parsedTokenValue < TOKEN_RANGE.MIDDLE_SOUND_TOKEN_AMOUNT;
    const tierFourTokens =
      parsedTokenValue >= TOKEN_RANGE.MIDDLE_SOUND_TOKEN_AMOUNT &&
      parsedTokenValue < TOKEN_RANGE.HIGH_SOUND_TOKEN_AMOUNT;
    const tierFiveTokens = parsedTokenValue >= TOKEN_RANGE.HIGH_SOUND_TOKEN_AMOUNT;
    if (tierOneTokens) {
      this.playSound(ThemeService.theme.tipping.tier1Sound);
    } else if (tierTwoTokens || tierThreeTokens) {
      this.playSound(ThemeService.theme.tipping.tier2Sound);
    } else if (tierFourTokens) {
      this.playSound(ThemeService.theme.tipping.tier3Sound);
    } else if (tierFiveTokens) {
      this.playSound(ThemeService.theme.tipping.tier4Sound);
    }
  };

  /**
   * Play boost sound if needed
   * @param {*} message Message object from Firebase
   */
  handleBoostNotificationSounds = (message) => {
    if (message.timestamp <= this.lastBoostMessageTimestamp) {
      return;
    }
    this.lastBoostMessageTimestamp = message.timestamp;
    this.playSound(ThemeService.theme.boost.sound);
  };

  handleGameNotificationSounds = (message) => {
    if (message.timestamp <= this.lastGameMessageTimestamp) {
      return;
    }
    this.lastGameMessageTimestamp = message.timestamp;
    if (['r', 'c'].includes(message?.gameOperation?.toLowerCase?.())) {
      if (message.gameName === GAMES.ROLL_THE_DICE) {
        this.playSound(SOUND_NOTIFICATION_TYPES.ROLL_THE_DICE);
      } else if (message.gameName === GAMES.SPIN_THE_WHEEL) {
        this.playSound(SOUND_NOTIFICATION_TYPES.SPIN_THE_WHEEL);
      } else if (message.gameName === GAMES.TOKEN_SQUARES) {
        this.playSound(SOUND_NOTIFICATION_TYPES.TOKEN_KENO);
      }
    } else {
      this.playSound(SOUND_NOTIFICATION_TYPES.GAME);
    }
  };

  playNotificationSound() {
    TippingSoundService.play(SOUND_NOTIFICATION_TYPES.PRIVATE_MESSAGE_SOUND, 1);
  }

  playSound = (sound) => {
    const isBroadcastWindow = location.pathname === '/broadcast';
    const volume = isBroadcastWindow ? 1 : this.tippingSoundVolume / 100;
    if ((this.tippingSoundVolume && !this.isTippingSoundMuted) || isBroadcastWindow) {
      TippingSoundService.play(sound, volume);
    }
  };
}

export default new BroadcastNotificationsService();
